import PropTypes from 'prop-types'
import React from 'react'
import cx from 'classnames'
import { connect } from 'react-redux'
import moment from 'moment'

import { Tooltip } from 'react-wot-bootstrap'
import Warning from 'components/Warning/Warning.jsx'
import { ReserveTooltipBody } from 'components/ReserveTooltip/components/ReserveTooltipBody'
import Timer from 'components/Timer/Timer.jsx'

import { toCssClass } from 'helpers'
import {
    canActivateReserves,
} from 'helpers/accounts'
import {
    BONUS_NOT_APPLIED_TO_RESERVIST,
    LEVEL_STRING,
    BUILDING_TITLES,
    RESERVE_TITLES,
} from 'helpers/i18n.jsx'
import {
    getActivatedReserve,
    getCurrentLevel,
    getReadyReserves,
    getBuildingTypeByReserve,
    getGroupByReserve,
    getIsReservesAbsent,
    findFirstActiveReserveInGroup,
    getTargetType,
} from 'helpers/reserves'

import {
    MAX_BUILDING_LEVEL,
    RESERVE_GROUPS,
    SPECIAL_CONDITIONS_X_LEVEL_ONLY,
} from 'constants'
import {
    RESERVE_COUNTER,
    RESERVE_BUILDING,
    ACTIVATABLE_IN_BATTLE_ROOM_DOES_NOT_HAVE_PERMISSION,
    ACTIVATABLE_IN_BATTLE_ROOM_INSTALL_IS_NOT_POSSIBLE,
    ACTIVATABLE_IN_BATTLE_ROOM_RESERVE_DOES_NOT_EXIST,
    ACTIVATABLE_IN_BATTLE_ROOM_READY_TO_INSTALL,
    RESERVE_IS_ACTIVATED,
    DOES_NOT_HAVE_PERMISSION,
    ACTIVATION_IS_IMPOSSIBLE,
    ANOTHER_RESERVE_IS_ACTIVATED,
    RESERVE_DOES_NOT_EXIST,
    READY_FOR_ACTIVATION,
    X_LEVEL_ONLY,
    FROM_CHALLENGES,
    EARN_LACKING_INDUSTRIAL_RESOURCE,
} from './constants.jsx'

import styles from './ReserveTooltip.scss'

export class ReserveTooltip extends React.Component {
    static propTypes = {
        id: PropTypes.string.isRequired,
        account: PropTypes.object.isRequired,
        clanReserves: PropTypes.object.isRequired,
        strongholdIsFrozen: PropTypes.bool.isRequired,
        reserveType: PropTypes.string,
        reserves: PropTypes.object,
        settings: PropTypes.object.isRequired,
    }

    static MESSAGES = {
        NO_PERMISSION: () => (
            <div className={cx(styles.statusMessage, styles.statusMessage__noPermission)}>
                <p className={styles.statusText}>{DOES_NOT_HAVE_PERMISSION}</p>
            </div>
        ),
        ACTIVATION_IS_IMPOSSIBLE: (reason) => (
            <div className={cx(styles.statusMessage, styles.statusMessage__negative)}>
                <p className={styles.status}>{ACTIVATION_IS_IMPOSSIBLE}</p>
                <p className={styles.statusText}>{reason}</p>
            </div>
        ),
        DISPOSABLE: (canActivate, reservesAreAbsent) => {
            if (!canActivate) {
                return (
                    <div className={cx(styles.statusMessage, styles.statusMessage__noPermission)}>
                        <p className={styles.statusText}>{ACTIVATABLE_IN_BATTLE_ROOM_DOES_NOT_HAVE_PERMISSION}</p>
                    </div>
                )
            } else if (reservesAreAbsent) {
                return (
                    <div className={cx(styles.statusMessage, styles.statusMessage__negative)}>
                        <p className={styles.status}>{ACTIVATABLE_IN_BATTLE_ROOM_INSTALL_IS_NOT_POSSIBLE}</p>
                        <p className={styles.statusText}>{ACTIVATABLE_IN_BATTLE_ROOM_RESERVE_DOES_NOT_EXIST}</p>
                    </div>
                )
            } else {
                return (
                    <div className={cx(styles.statusMessage, styles.statusMessage__positive)}>
                        <p className={styles.status}>{ACTIVATABLE_IN_BATTLE_ROOM_READY_TO_INSTALL}</p>
                    </div>
                )
            }
        },
        FIRST_ACTIVE_RESERVE_IN_GROUP: (firstActiveReserveInGroup, canActivate, reserveType) => {
            if (firstActiveReserveInGroup.type === reserveType) {
                const endTime = moment(firstActiveReserveInGroup.effectEnds)
                const timer = <Timer endTime={endTime} />
                return (
                    <div className={cx(styles.statusMessage, styles.statusMessage__positive)}>
                        <p className={styles.status}>{RESERVE_IS_ACTIVATED(timer)}</p>
                    </div>
                )
            } else {
                if (!canActivate) {
                    return ReserveTooltip.MESSAGES.NO_PERMISSION()
                } else {
                    return ReserveTooltip.MESSAGES.ACTIVATION_IS_IMPOSSIBLE(ANOTHER_RESERVE_IS_ACTIVATED)
                }
            }
        },
    }

    getMessage() {
        const reservesAreAbsent = getIsReservesAbsent(this.props.reserves)
        const reserveGroup = getGroupByReserve(this.props.reserveType, this.props.settings)
        const firstActiveReserveInGroup = findFirstActiveReserveInGroup(
            this.props.clanReserves,
            this.props.settings.reserve_groups[reserveGroup]
        )
        const canActivate = canActivateReserves(this.props.account)

        let message
        if (reserveGroup === RESERVE_GROUPS.DISPOSABLE) {
            message = ReserveTooltip.MESSAGES.DISPOSABLE(canActivate, reservesAreAbsent)
        } else if (this.props.strongholdIsFrozen) {
            if (!canActivate) {
                message = ReserveTooltip.MESSAGES.NO_PERMISSION()
            } else {
                message = ReserveTooltip.MESSAGES.ACTIVATION_IS_IMPOSSIBLE(EARN_LACKING_INDUSTRIAL_RESOURCE)
            }
        } else if (firstActiveReserveInGroup) {
            message = ReserveTooltip.MESSAGES.FIRST_ACTIVE_RESERVE_IN_GROUP(
                firstActiveReserveInGroup,
                canActivate,
                this.props.reserveType
            )
        } else if (!canActivate) {
            message = ReserveTooltip.MESSAGES.NO_PERMISSION()
        } else if (reservesAreAbsent) {
            message = ReserveTooltip.MESSAGES.ACTIVATION_IS_IMPOSSIBLE(RESERVE_DOES_NOT_EXIST)
        } else {
            message = (
                <div className={cx(styles.statusMessage, styles.statusMessage__positive)}>
                    <p className={styles.status}>{READY_FOR_ACTIVATION}</p>
                </div>
            )
        }
        return message
    }

    render() {
        if (!this.props.reserveType) {
            return null
        }
        const reserveInfo = this.props.settings.reserves[this.props.reserveType]
        const activatedReserve = getActivatedReserve(this.props.reserves)
        const currentLevel = getCurrentLevel(activatedReserve, this.props.reserves)
        const currentLevelReserves = this.props.reserves[currentLevel]
        const readyReserves = getReadyReserves(currentLevelReserves)

        const canActivate = canActivateReserves(this.props.account)

        const buildingType = getBuildingTypeByReserve(this.props.reserveType, this.props.settings)
        const buildingName = BUILDING_TITLES[buildingType]

        const currentBonus = reserveInfo.bonus.values[currentLevel - 1]

        const reserveGroup = getGroupByReserve(this.props.reserveType, this.props.settings)
        const bonusValuesArray = Object.values(currentBonus.battle_types)
        const targetType = getTargetType(reserveGroup, bonusValuesArray)
        const className = cx(styles.base,
            styles[`base__type-${targetType}`]
        )

        const imageClassName = cx(
            styles.image,
            styles[`image__${toCssClass(this.props.reserveType)}`], {
                [styles.image__elite] : currentLevel > MAX_BUILDING_LEVEL,
            }
        )

        let counterInfo
        if (readyReserves.length > 0) {
            const counter = (
                <span className={styles.counter}>{readyReserves.length}</span>
            )
            counterInfo = (
                <p className={styles.counterInfo}>{RESERVE_COUNTER(counter)}</p>
            )
        }

        const isSpecialCondition = currentBonus.special_conditions.includes(SPECIAL_CONDITIONS_X_LEVEL_ONLY)

        const buildingMessage = (currentLevel <= MAX_BUILDING_LEVEL && canActivate) ? (
            <div className={styles.reserveMessage}>
                {RESERVE_BUILDING(buildingName)}
            </div>
        ) : null

        const specialConditionMessage = isSpecialCondition ? (
            <div className={styles.reserveMessage}>
                <Warning>
                    {X_LEVEL_ONLY}
                </Warning>
            </div>
        ) : null

        const challengesMessage = (currentLevel > MAX_BUILDING_LEVEL && canActivate) ? (
            <div className={styles.reserveMessage}>
                {FROM_CHALLENGES}
            </div>
        ) : null

        const reservistBonusMessage = BONUS_NOT_APPLIED_TO_RESERVIST

        const bonusNotAppliedToReservistMessage = (reserveGroup === RESERVE_GROUPS.DISPOSABLE) ?
            null : (
                <div className={styles.reserveMessage}>
                    {reservistBonusMessage}
                </div>
            )

        const specialConditions = (() => {
            if (isSpecialCondition || canActivate) {
                const conditions = [
                    buildingMessage,
                    bonusNotAppliedToReservistMessage,
                    specialConditionMessage,
                    challengesMessage,
                ].filter(Boolean)

                return conditions.length ? conditions : null
            }

            return bonusNotAppliedToReservistMessage
        })()

        return (
            <Tooltip id={this.props.id} isWide>
                <div className={className}>
                    <div className={styles.header}>
                        <div className={styles.button}>
                            <div className={styles.thumbnail}>
                                <div className={imageClassName} />
                            </div>
                        </div>
                        <div className={styles.info}>
                            <p className={styles.title}>{RESERVE_TITLES[reserveInfo.type]}</p>
                            <p className={styles.level}>{LEVEL_STRING(currentLevel)}</p>
                            {counterInfo}
                        </div>
                    </div>
                    <div className={styles.content}>
                        <ReserveTooltipBody
                            reserveType={this.props.reserveType}
                            reserveInfo={reserveInfo}
                            level={currentLevel}
                        />
                        {specialConditions && (
                            <div className={styles.reserveMessages}>
                                {currentBonus.parameters && <div className={styles.divider} />}
                                {specialConditions}
                            </div>
                        )}
                        {(specialConditions || currentBonus.parameters) && <div className={styles.divider} />}
                        {this.getMessage()}
                    </div>
                </div>
            </Tooltip>
        )
    }
}

export const mapStateToProps = (state, { id, reservesByLevel, reserveType = null, reserves = null }) => {
    const { clanReserves, account, settings, toolbar, clan } = state
    const tooltipReserveType = reserveType || toolbar.tooltipReserveType
    const strongholdIsFrozen = clan ? clan.industrial_resource < 0 : false
    return {
        id,
        account,
        clanReserves,
        strongholdIsFrozen,
        reserveType: tooltipReserveType,
        reserves: reserves || reservesByLevel[tooltipReserveType],
        settings,
    }
}

export default connect(mapStateToProps)(ReserveTooltip)



// WEBPACK FOOTER //
// ./containers/Stronghold/components/ReserveTooltip/ReserveTooltip.jsx